import Vue from 'vue';
import VueI18n from 'vue-i18n';
import * as Sentry from '@sentry/vue';
import { initializeApp } from 'firebase/app';
import { getAnalytics } from 'firebase/analytics';
import { getAuth, onIdTokenChanged } from 'firebase/auth';
import moment from 'vue-moment';
import momentTimeZone from 'moment-timezone';
import en from './i18n/en';
import es from './i18n/es';
import App from './App.vue';
import router from './router';
import backendV2Service from './services/backend-v2.service';
import store from './store';
import vuetify from './plugins/vuetify';
import registerFilters from './filters';
import { startVersionCheck } from './services/version-check';
import {
  DEFAULT_SENTRY_DSN,
  SENTRY_IGNORE_ERRORS,
  dropHandledErrors,
  resolveSentryEnvironment,
  shouldInitSentry,
} from './utils/sentry-filter';

registerFilters(Vue);

// Multi Language Add
Vue.use(VueI18n);
const messages = { en, es };
const defaultLocale = 'es';
const i18n = new VueI18n({
  locale: defaultLocale,
  fallbackLocale: 'en',
  messages,
});

Vue.config.productionTip = false;

// [KB-990] Monitoreo de errores con GlitchTip (SDK de Sentry 7, el último con Vue 2).
const sentryEnvironment = resolveSentryEnvironment(process.env.VUE_APP_SENTRY_ENVIRONMENT);
if (shouldInitSentry(sentryEnvironment, process.env.NODE_ENV)) {
  Sentry.init({
    Vue,
    dsn: process.env.VUE_APP_SENTRY_DSN || DEFAULT_SENTRY_DSN,
    environment: sentryEnvironment,
    integrations: [Sentry.browserTracingIntegration({ router })],
    tracesSampleRate: 0.1,
    sendDefaultPii: false,
    // Los props de los componentes del admin traen datos de alumnos y docentes.
    attachProps: false,
    // Sentry reemplaza el errorHandler de Vue; seguir logueando en consola.
    logErrors: true,
    ignoreErrors: SENTRY_IGNORE_ERRORS,
    beforeSend: dropHandledErrors,
  });
}

Vue.use(moment, momentTimeZone);

const firebaseConfig = {
  apiKey: process.env.VUE_APP_API_KEY,
  authDomain: process.env.VUE_APP_AUTH_DOMAIN,
  projectId: process.env.VUE_APP_PROJECT_ID,
  storageBucket: process.env.VUE_APP_STORAGE_BUCKET,
  messagingSenderId: process.env.VUE_APP_MESSAGING_SENDER_ID,
  appId: process.env.VUE_APP_APP_ID,
  measurementId: process.env.VUE_APP_MEASUREMENT_ID,
};

const app = initializeApp(firebaseConfig);
getAnalytics(app);

// Keep Firebase ID token in sync with Vuex/localStorage.
// This keeps BaseService requests valid after token refresh.
const auth = getAuth();

function safeReplace(path: string): void {
  if (router.currentRoute.path === path) {
    return;
  }

  router.replace(path).catch((error: unknown) => {
    const navigationError = error as { name?: string } | null;
    if (navigationError?.name !== 'NavigationDuplicated') {
      throw error;
    }
  });
}

onIdTokenChanged(auth, async (user) => {
  if (user) {
    const idToken = await user.getIdToken();
    store.commit('SET_USER_TOKEN', idToken);
    // Renew session cookie (non-blocking, best-effort)
    await backendV2Service.createSession(idToken).catch((error) => {
      console.error('Failed to refresh session cookie:', error);
    });
  } else if (store.state.userLogged) {
    // Firebase session lost while store still thinks user is logged in -> force logout
    store.dispatch('signOut');
    safeReplace('/login');
  }
});

new Vue({
  i18n,
  router,
  store,
  vuetify,
  render: (h) => h(App),
}).$mount('#app');

// Detección de versión nueva: al detectarla, mostrar el modal que recarga.
startVersionCheck(router, () => {
  store.dispatch('versionUpdate/show');
});
